/* Resin Anvil theme helpers: release carousel, filter toggle, bundle picker. */
(() => {
  /* ---------- Release carousel ---------- */
  if (!customElements.get('ra-carousel')) {
    class RaCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.ra-carousel__track');
        this.buttons = this.querySelectorAll('.ra-carousel__button');
        if (!this.track) return;
        this.buttons.forEach((button) =>
          button.addEventListener('click', () => this.go(button.dataset.direction === 'next' ? 1 : -1))
        );
        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update, { passive: true });
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.update);
      }

      go(direction) {
        const slide = this.track.querySelector('.ra-carousel__slide');
        const step = slide ? slide.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: direction * step, behavior: reduce ? 'auto' : 'smooth' });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        const [prev, next] = this.buttons;
        if (prev) prev.disabled = this.track.scrollLeft <= 2;
        if (next) next.disabled = this.track.scrollLeft >= max;
        this.classList.toggle('ra-carousel--static', max <= 0);
      }
    }
    customElements.define('ra-carousel', RaCarousel);
  }

  /* ---------- Collection "Hide filters" toggle ---------- */
  const FILTER_KEY = 'ra-filters-hidden';
  const applyFilterState = (hidden) => {
    document.querySelectorAll('[data-ra-filters-container]').forEach((el) => el.classList.toggle('ra-filters-hidden', hidden));
    document.querySelectorAll('.ra-filter-toggle').forEach((button) => {
      button.setAttribute('aria-expanded', String(!hidden));
      const label = button.querySelector('.ra-filter-toggle__label');
      if (label) label.textContent = hidden ? button.dataset.labelShow : button.dataset.labelHide;
    });
  };
  const initFilterToggle = () => {
    if (!document.querySelector('.ra-filter-toggle')) return;
    let hidden = false;
    try {
      hidden = window.localStorage.getItem(FILTER_KEY) === '1';
    } catch (e) {}
    applyFilterState(hidden);
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.ra-filter-toggle');
    if (!button) return;
    const hidden = button.getAttribute('aria-expanded') === 'true';
    applyFilterState(hidden);
    try {
      window.localStorage.setItem(FILTER_KEY, hidden ? '1' : '0');
    } catch (e) {}
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterToggle);
  } else {
    initFilterToggle();
  }
  document.addEventListener('shopify:section:load', initFilterToggle);

  /* ---------- Bundle picker ---------- */
  if (!customElements.get('ra-bundle')) {
    class RaBundle extends HTMLElement {
      connectedCallback() {
        this.submitButton = this.querySelector('[data-ra-bundle-submit]');
        this.status = this.querySelector('[data-status]');
        this.countEl = this.querySelector('[data-count]');
        this.scaleOption = this.dataset.scaleOption || 'scale';
        try {
          this.currentMap = JSON.parse(this.querySelector('[data-ra-bundle-current]').textContent);
        } catch (e) {
          this.currentMap = {};
        }

        this.addEventListener('change', (event) => {
          if (event.target.classList.contains('ra-bundle__checkbox')) this.updateCount();
        });
        this.submitButton?.addEventListener('click', () => this.addToCart());
        // Browsers can restore ticked boxes on refresh/back without firing change.
        window.addEventListener('pageshow', () => this.updateCount());

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId && event.data.sectionId !== this.dataset.sectionId) return;
            this.syncScale();
          });
        }
        this.syncScale();
        this.updateCount();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      get currentVariantId() {
        const input = document.querySelector(`#product-form-${this.dataset.sectionId} input[name="id"]`);
        return input ? input.value : null;
      }

      get currentScale() {
        const id = this.currentVariantId;
        return id && this.currentMap[id] ? this.currentMap[id] : '';
      }

      /* Pick each listed product's variant matching the selected scale (else first available). */
      syncScale() {
        const scale = this.currentScale;
        this.querySelectorAll('.ra-bundle__checkbox[data-variants]').forEach((checkbox) => {
          let variants = [];
          try {
            variants = JSON.parse(checkbox.dataset.variants);
          } catch (e) {
            return;
          }
          const available = variants.filter((v) => v.available);
          const match = (scale && available.find((v) => v.scale === scale)) || available[0] || variants[0];
          if (!match) return;
          checkbox.value = match.id;
          const price = checkbox.closest('.ra-bundle__item')?.querySelector('[data-price]');
          if (price && match.price) price.textContent = match.price;
        });
      }

      selectedItems() {
        const items = [];
        this.querySelectorAll('.ra-bundle__checkbox:checked').forEach((checkbox) => {
          const id = checkbox.hasAttribute('data-current') ? this.currentVariantId : checkbox.value;
          if (id) items.push({ id: Number(id), quantity: 1 });
        });
        return items;
      }

      updateCount() {
        const count = this.selectedItems().length;
        if (this.countEl) this.countEl.textContent = count ? `(${count})` : '';
        if (this.submitButton) this.submitButton.disabled = count === 0;
      }

      async addToCart() {
        const items = this.selectedItems();
        if (!items.length) return;
        const cartDrawer = document.querySelector('cart-drawer');
        const spinner = this.submitButton.querySelector('.loading__spinner');
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
        spinner?.classList.remove('hidden');
        this.status.textContent = '';

        const body = { items };
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(`${this.dataset.addUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            this.status.textContent = data.description || data.message || window.cartStrings?.error || 'Error';
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ra-bundle', cartData: data });
          }
          if (cartDrawer && data.sections) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents({ id: data.items?.[0]?.id, sections: data.sections });
          } else {
            window.location = this.dataset.cartUrl;
          }
        } catch (error) {
          this.status.textContent = window.cartStrings?.error || 'Error';
        } finally {
          spinner?.classList.add('hidden');
          this.submitButton.removeAttribute('aria-busy');
          this.updateCount();
        }
      }
    }
    customElements.define('ra-bundle', RaBundle);
  }
})();
